/** Keeps a deadline active until the operation, including body consumption, settles. */
export async function withRequestTimeout<T>(
  operation: (signal: AbortSignal) => Promise<T>,
  { timeoutMs = 30_000, signal }: { timeoutMs?: number; signal?: AbortSignal | null } = {}
): Promise<T> {
  const controller = new AbortController();
  const forwardAbort = () =>
    controller.abort(signal?.reason ?? new DOMException('Request aborted', 'AbortError'));
  const timer = setTimeout(
    () => controller.abort(new DOMException('Request timed out', 'TimeoutError')),
    timeoutMs
  );
  let rejectAbort: () => void = () => {};
  try {
    const aborted = new Promise<never>((_resolve, reject) => {
      rejectAbort = () => reject(controller.signal.reason);
      controller.signal.addEventListener('abort', rejectAbort, { once: true });
    });
    if (signal?.aborted) forwardAbort();
    else signal?.addEventListener('abort', forwardAbort, { once: true });
    // Custom transports and body readers may ignore abort signals, so also race the work.
    if (controller.signal.aborted) return await aborted;
    return await Promise.race([operation(controller.signal), aborted]);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', forwardAbort);
    controller.signal.removeEventListener('abort', rejectAbort);
  }
}

/** Bounds response headers; wrap body consumption in withRequestTimeout for a full reply. */
export function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit & { timeoutMs?: number }
): Promise<Response> {
  const { timeoutMs, ...fetchInit } = init ?? {};
  return withRequestTimeout(
    signal => {
      const external = fetchInit.signal;
      // Keep the caller connected to the body even after the header deadline is disposed.
      let bodySignal = signal;
      if (external) {
        if (typeof AbortSignal.any === 'function') bodySignal = AbortSignal.any([signal, external]);
        else {
          const linked = new AbortController();
          for (const source of [signal, external]) {
            if (source.aborted) linked.abort(source.reason);
            else
              source.addEventListener('abort', () => linked.abort(source.reason), { once: true });
          }
          bodySignal = linked.signal;
        }
      }
      return fetch(input, { ...fetchInit, signal: bodySignal });
    },
    {
      timeoutMs,
      signal: fetchInit.signal,
    }
  );
}
